import React from "react";
import { useTranslation } from "react-i18next";

import { HEADER_VALUE } from "../AssignStakeholders.constants";

import { Box, Button, Checkbox, Radio, Typography } from "@components/base";
import Table, { TableColumn } from "@components/common/Table";
import type { QuestionItemType } from "@api";
import IconAssignUsers from "@icons/AssignUsers";

type StringSet = Set<string>;

type Row = { id: string; value: string; label: string; count: number };

interface StakeholdersSelectionProps {
  allQuestions: QuestionItemType[];
  stakeholderTypeOptions: string[];
  selectedStakeholderType: string;
  onToggle: (value: string) => void;
  onAssign: (level: "stakeholder", label: string) => void;
  sectionRef?: React.RefObject<HTMLDivElement>;
}

const StakeholdersSelection: React.FC<StakeholdersSelectionProps> = ({
  allQuestions,
  stakeholderTypeOptions,
  selectedStakeholderType,
  onToggle,
  onAssign,
  sectionRef,
}) => {
  const { t } = useTranslation();

  const rows: Row[] = stakeholderTypeOptions
    .map((value) => ({
      id: value,
      value,
      label:
        value === "internal"
          ? t("stakeholders.type.internal", "Internal")
          : t("stakeholders.type.external", "External"),
      count: allQuestions.filter((q) => String(q.stakeholderType) === value).length,
    }))
    .filter((row) => row.count > 0);

  const columns: TableColumn<Row>[] = [
    {
      title: t("common.select", "Select"),
      key: "select",
      renderCellContent: (row) => {
        if (row.value === HEADER_VALUE) {
          return null;
        }

        return (
          <Checkbox
            checked={selectedStakeholderType === row.value}
            onChange={() => onToggle(row.value)}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          />
        );
      },
      tableCellProps: { align: "center", sx: { width: 80 } },
    },
    {
      title: t("common.name", "Name"),
      key: "label",
      renderCellContent: (row) => (
        <Typography variant={row.value === HEADER_VALUE ? "subtitle2" : "body2"}>
          {row.label}
        </Typography>
      ),
    },
    {
      title: t("assignType.button", "Assign type"),
      key: "assign",
      renderCellContent: (row) => {
        if (row.value === HEADER_VALUE) {
          return null;
        }

        return (
          <Button
            variant="text"
            size="small"
            startIcon={<IconAssignUsers />}
            onClick={(e) => {
              e.stopPropagation();
              const label =
                row.value === HEADER_VALUE ? t("common.all", "All") : row.label;

              onAssign("stakeholder", label);
            }}
            disabled={selectedStakeholderType !== row.value}
          >
            {t("assignType.button", "Assign type")}
          </Button>
        );
      },
      tableCellProps: { align: "right", sx: { width: 180 } },
    },
  ];

  return (
    <Box ref={sectionRef}>
      <Table
        columns={columns}
        data={[
          {
            id: "",
            value: HEADER_VALUE,
            label: t("stakeholders.table.stakeholderType", "Stakeholder type"),
            count: 0,
          },
          ...rows,
        ]}
        tableProps={{ size: "small", sx: { "& thead": { display: "none" } } }}
        onRowClick={(row) =>
          row.value === HEADER_VALUE ? undefined : onToggle(row.value)
        }
      />
    </Box>
  );
};

export default StakeholdersSelection;
